import React, { useState, useEffect } from 'react';
import { Building2, Users, Shield, Plus, Pencil, FileSignature, Trash2, Users2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import DecimalInput from '../components/DecimalInput';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

const Admin = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('users');
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const modal = useModal();
  const [usersViewMode, setUsersViewMode] = useViewMode('admin-users');
  const [groupsViewMode, setGroupsViewMode] = useViewMode('admin-groups');
  const flUsers = useFieldLayout('admin-users', usersViewMode);
  const flGroups = useFieldLayout('admin-groups', groupsViewMode);

  // Modal states
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showTenantEditModal, setShowTenantEditModal] = useState(false);
  const [showUserEditModal, setShowUserEditModal] = useState(false);
  const [showRoleEditModal, setShowRoleEditModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showGroupEditModal, setShowGroupEditModal] = useState(false);
  const [editingTenantId, setEditingTenantId] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [tenantForm, setTenantForm] = useState({ name: '', slug: '', nif: '', address: '', city: '', postalCode: '', phone: '', email: '' });
  const [userForm, setUserForm] = useState({ name: '', email: '', phone: '', password: '', isSuperAdmin: false, tenantAssignments: [], groupIds: [] });
  const [groupForm, setGroupForm] = useState({ name: '', description: '', userIds: [] });
  const [groupEditForm, setGroupEditForm] = useState({ name: '', description: '', userIds: [] });
  const [tenantEditForm, setTenantEditForm] = useState({
    name: '', nif: '', address: '', city: '', province: '', postalCode: '', phone: '', email: '', isActive: true,
    default_tax_rate: '21', default_irpf_rate: '15',
    billing_bank_name: '', billing_bank_iban: '',
    invoice_prefix: '',
    mail_host: '', mail_port: '', mail_secure: 'false', mail_user: '', mail_password: '', mail_from_name: '', mail_from_email: '',
    mail_imap_host: '', mail_imap_port: '993', mail_imap_secure: 'true', mail_imap_user: '', mail_imap_password: '', mail_imap_sent_mailbox: '',
    sync_asesoria_token: ''
  });
  const [userEditForm, setUserEditForm] = useState({ name: '', email: '', phone: '', password: '', isSuperAdmin: false, isActive: true, groupIds: [] });
  const [roleEditForm, setRoleEditForm] = useState({ name: '', description: '', rank: 100, permissions: [] });

  // Catálogos de contratos (tipos y estados)
  const [contractTypes, setContractTypes] = useState([]);
  const [contractStatuses, setContractStatuses] = useState([]);
  const [contractsCatalogsLoaded, setContractsCatalogsLoaded] = useState(false);
  const [newContractType, setNewContractType] = useState({ name: '', sortOrder: '0' });
  const [newContractStatus, setNewContractStatus] = useState({ name: '', sortOrder: '0' });

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (tab !== 'contracts' || contractsCatalogsLoaded) return;
    (async () => {
      try {
        const [typesRes, statusesRes] = await Promise.all([
          api.get('/contract-types'),
          api.get('/contract-statuses')
        ]);
        setContractTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
        setContractStatuses(Array.isArray(statusesRes.data) ? statusesRes.data : []);
        setContractsCatalogsLoaded(true);
      } catch {
        modal.error('Error cargando catálogos de contratos');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const reloadContractTypes = async () => {
    const res = await api.get('/contract-types');
    setContractTypes(Array.isArray(res.data) ? res.data : []);
  };

  const reloadContractStatuses = async () => {
    const res = await api.get('/contract-statuses');
    setContractStatuses(Array.isArray(res.data) ? res.data : []);
  };

  const handleCreateContractType = async (e) => {
    e.preventDefault();
    if (!newContractType.name.trim()) return;
    try {
      await api.post('/contract-types', {
        name: newContractType.name.trim(),
        sortOrder: Number.parseInt(newContractType.sortOrder, 10) || 0
      });
      setNewContractType({ name: '', sortOrder: '0' });
      await reloadContractTypes();
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error creando tipo');
    }
  };

  const handleUpdateContractType = async (id, patch) => {
    try {
      await api.put(`/contract-types/${id}`, patch);
      await reloadContractTypes();
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando tipo');
    }
  };

  const handleDeleteContractType = (item) => {
    modal.confirm(
      `¿Eliminar el tipo "${item.name}"?`,
      async () => {
        try {
          await api.delete(`/contract-types/${item.id}`);
          await reloadContractTypes();
        } catch (error) {
          modal.error(error.response?.data?.message || 'Error eliminando tipo');
        }
      }
    );
  };

  const handleCreateContractStatus = async (e) => {
    e.preventDefault();
    if (!newContractStatus.name.trim()) return;
    try {
      await api.post('/contract-statuses', {
        name: newContractStatus.name.trim(),
        sortOrder: Number.parseInt(newContractStatus.sortOrder, 10) || 0
      });
      setNewContractStatus({ name: '', sortOrder: '0' });
      await reloadContractStatuses();
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error creando estado');
    }
  };

  const handleUpdateContractStatus = async (id, patch) => {
    try {
      await api.put(`/contract-statuses/${id}`, patch);
      await reloadContractStatuses();
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando estado');
    }
  };

  const handleDeleteContractStatus = (item) => {
    modal.confirm(
      `¿Eliminar el estado "${item.name}"?`,
      async () => {
        try {
          await api.delete(`/contract-statuses/${item.id}`);
          await reloadContractStatuses();
        } catch (error) {
          modal.error(error.response?.data?.message || 'Error eliminando estado');
        }
      }
    );
  };

  const loadData = async () => {
    try {
      const [t, u, r, m, g] = await Promise.all([
        api.get('/admin/tenants'),
        api.get('/admin/users'),
        api.get('/admin/roles'),
        api.get('/admin/modules'),
        api.get('/admin/groups')
      ]);
      setTenants(t.data);
      setUsers(u.data);
      setRoles(r.data);
      setModules(m.data);
      setGroups(g.data);
    } catch (error) {
      modal.error('Error cargando datos de administración');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/admin/tenants', tenantForm);
      setTenants([res.data, ...tenants]);
      setShowTenantModal(false);
      setTenantForm({ name: '', slug: '', nif: '', address: '', city: '', postalCode: '', phone: '', email: '' });
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error creando empresa');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/admin/users', userForm);
      setUsers([res.data, ...users]);
      setShowUserModal(false);
      setUserForm({ name: '', email: '', phone: '', password: '', isSuperAdmin: false, tenantAssignments: [], groupIds: [] });
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error creando usuario');
    }
  };

  const openTenantEdit = async (tenant) => {
    setEditingTenantId(tenant.id);
    setTenantEditForm({
      name: tenant.name,
      nif: tenant.nif || '',
      address: tenant.address || '',
      city: tenant.city || '',
      province: tenant.province || '',
      postalCode: tenant.postalCode || '',
      phone: tenant.phone || '',
      email: tenant.email || '',
      isActive: tenant.isActive === true,
      default_tax_rate: '21', default_irpf_rate: '15',
      billing_bank_name: '', billing_bank_iban: '',
      sync_asesoria_token: '',
      mail_host: '', mail_port: '', mail_secure: 'false', mail_user: '', mail_password: '', mail_from_name: '', mail_from_email: '',
      mail_imap_host: '', mail_imap_port: '993', mail_imap_secure: 'true', mail_imap_user: '', mail_imap_password: '', mail_imap_sent_mailbox: ''
    });
    try {
      const res = await api.get(`/admin/tenants/${tenant.id}/settings`);
      const list = Array.isArray(res.data) ? res.data : [];
      const get = (key, def) => list.find(s => s.key === key)?.value ?? def;
      setTenantEditForm(f => ({
        ...f,
        default_tax_rate: get('default_tax_rate', '21'),
        default_irpf_rate: get('default_irpf_rate', '15'),
        billing_bank_name: get('billing_bank_name', ''),
        billing_bank_iban: get('billing_bank_iban', ''),
        mail_host: get('mail_host', ''),
        mail_port: get('mail_port', ''),
        mail_secure: get('mail_secure', 'false'),
        mail_user: get('mail_user', ''),
        mail_password: get('mail_password', ''),
        mail_from_name: get('mail_from_name', ''),
        mail_from_email: get('mail_from_email', ''),
        mail_imap_host: get('mail_imap_host', ''),
        mail_imap_port: get('mail_imap_port', '993'),
        mail_imap_secure: get('mail_imap_secure', 'true'),
        mail_imap_user: get('mail_imap_user', ''),
        mail_imap_password: get('mail_imap_password', ''),
        mail_imap_sent_mailbox: get('mail_imap_sent_mailbox', ''),
        sync_asesoria_token: get('sync_asesoria_token', '')
      }));
    } catch {
      // settings no críticos
    }
    try {
      const prefixRes = await api.get(`/admin/tenants/${tenant.id}/invoice-prefix`);
      setTenantEditForm(f => ({ ...f, invoice_prefix: prefixRes.data.prefix ?? '' }));
    } catch {
      // serie sin configurar
    }
    setShowTenantEditModal(true);
  };

  const openUserEdit = (u) => {
    setEditingUserId(u.id);
    setUserEditForm({
      name: u.name,
      email: u.email,
      phone: u.phone || '',
      password: '',
      isSuperAdmin: u.isSuperAdmin === true,
      isActive: u.isActive === true,
      groupIds: (u.groups || []).map(m => m.group?.id).filter(Boolean)
    });
    setShowUserEditModal(true);
  };

  const openRoleEdit = (role) => {
    setEditingRoleId(role.id);
    setRoleEditForm({
      name: role.name,
      description: role.description || '',
      rank: Number.isInteger(role.rank) ? role.rank : 100,
      permissions: role.permissions.map((p) => ({
        moduleId: p.moduleId,
        moduleLabel: p.module?.label || '',
        canView: p.canView,
        canCreate: p.canCreate,
        canEdit: p.canEdit,
        canDelete: p.canDelete,
        canExport: p.canExport
      }))
    });
    setShowRoleEditModal(true);
  };

  const openGroupEdit = (group) => {
    setEditingGroupId(group.id);
    setGroupEditForm({
      name: group.name,
      description: group.description || '',
      userIds: (group.members || []).map(m => m.user?.id).filter(Boolean)
    });
    setShowGroupEditModal(true);
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!groupForm.name.trim()) return;
    try {
      const res = await api.post('/admin/groups', {
        name: groupForm.name.trim(),
        description: groupForm.description,
        userIds: groupForm.userIds
      });
      setGroups([res.data, ...groups]);
      setShowGroupModal(false);
      setGroupForm({ name: '', description: '', userIds: [] });
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error creando grupo');
    }
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    try {
      const res = await api.put(`/admin/groups/${editingGroupId}`, {
        name: groupEditForm.name.trim(),
        description: groupEditForm.description,
        userIds: groupEditForm.userIds
      });
      setGroups(groups.map(g => g.id === editingGroupId ? res.data : g));
      setShowGroupEditModal(false);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando grupo');
    }
  };

  const handleDeleteGroup = (group) => {
    modal.confirm(`¿Eliminar el grupo "${group.name}"?`, async () => {
      try {
        await api.delete(`/admin/groups/${group.id}`);
        setGroups(groups.filter(g => g.id !== group.id));
      } catch (error) {
        modal.error(error.response?.data?.message || 'Error eliminando grupo');
      }
    });
  };

  const handleUpdateTenant = async (event) => {
    event.preventDefault();
    try {
      const {
        default_tax_rate, default_irpf_rate, billing_bank_name, billing_bank_iban, invoice_prefix,
        mail_host, mail_port, mail_secure, mail_user, mail_password, mail_from_name, mail_from_email,
        mail_imap_host, mail_imap_port, mail_imap_secure, mail_imap_user, mail_imap_password, mail_imap_sent_mailbox,
        sync_asesoria_token,
        ...tenantData
      } = tenantEditForm;
      const res = await api.put(`/admin/tenants/${editingTenantId}`, tenantData);
      setTenants(tenants.map((tenant) => (tenant.id === editingTenantId ? { ...tenant, ...res.data } : tenant)));
      await api.put(`/admin/tenants/${editingTenantId}/settings/bulk`, {
        settings: [
          { key: 'default_tax_rate', value: default_tax_rate, group: 'billing' },
          { key: 'default_irpf_rate', value: default_irpf_rate, group: 'billing' },
          { key: 'billing_bank_name', value: billing_bank_name, group: 'billing' },
          { key: 'billing_bank_iban', value: billing_bank_iban, group: 'billing' },
          { key: 'mail_host', value: mail_host, group: 'mail' },
          { key: 'mail_port', value: mail_port, group: 'mail' },
          { key: 'mail_secure', value: mail_secure, group: 'mail' },
          { key: 'mail_user', value: mail_user, group: 'mail' },
          { key: 'mail_password', value: mail_password, group: 'mail' },
          { key: 'mail_from_name', value: mail_from_name, group: 'mail' },
          { key: 'mail_from_email', value: mail_from_email, group: 'mail' },
          { key: 'mail_imap_host', value: mail_imap_host, group: 'mail' },
          { key: 'mail_imap_port', value: mail_imap_port, group: 'mail' },
          { key: 'mail_imap_secure', value: mail_imap_secure, group: 'mail' },
          { key: 'mail_imap_user', value: mail_imap_user, group: 'mail' },
          { key: 'mail_imap_password', value: mail_imap_password, group: 'mail' },
          { key: 'mail_imap_sent_mailbox', value: mail_imap_sent_mailbox, group: 'mail' },
          { key: 'sync_asesoria_token', value: sync_asesoria_token, group: 'sync' }
        ]
      });
      if (invoice_prefix) {
        await api.put(`/admin/tenants/${editingTenantId}/invoice-prefix`, { prefix: invoice_prefix });
      }
      setShowTenantEditModal(false);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando empresa');
    }
  };

  const handleUpdateUser = async (event) => {
    event.preventDefault();
    try {
      const payload = { ...userEditForm };
      if (!payload.password) delete payload.password;
      const res = await api.put(`/admin/users/${editingUserId}`, payload);
      setUsers(users.map((u) => (u.id === editingUserId ? { ...u, ...res.data } : u)));
      setShowUserEditModal(false);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando usuario');
    }
  };

  const updateRolePermission = (moduleId, field, value) => {
    setRoleEditForm((prev) => ({
      ...prev,
      permissions: prev.permissions.map((permission) =>
        permission.moduleId === moduleId ? { ...permission, [field]: value } : permission
      )
    }));
  };

  const handleUpdateRole = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        name: roleEditForm.name,
        description: roleEditForm.description,
        rank: Number.parseInt(roleEditForm.rank, 10),
        permissions: roleEditForm.permissions.map((permission) => ({
          moduleId: permission.moduleId,
          canView: permission.canView,
          canCreate: permission.canCreate,
          canEdit: permission.canEdit,
          canDelete: permission.canDelete,
          canExport: permission.canExport
        }))
      };
      const res = await api.put(`/admin/roles/${editingRoleId}`, payload);
      setRoles(roles.map((role) => (role.id === editingRoleId ? res.data : role)));
      setShowRoleEditModal(false);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando rol');
    }
  };

  if (!user?.isSuperAdmin) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Acceso denegado. Se requiere Super Admin.</div>;
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>;

  const userColumns = [
    { key: 'name', header: 'Nombre', className: 'dt-strong', fixed: true, render: (u) => u.name },
    { key: 'email', header: 'Email', render: (u) => u.email || '—' },
    { key: 'phone', header: 'Teléfono', className: 'dt-nowrap', render: (u) => u.phone || '—' },
    { key: 'tenants', header: 'Empresas', render: (u) => u.tenants?.map(t => t.tenant?.name).join(', ') || '—' },
    { key: 'groups', header: 'Grupos', render: (u) => (u.groups || []).map(g => g.group?.name).filter(Boolean).join(', ') || '—' },
    { key: 'status', header: 'Estado', render: (u) => (
      <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
        <span className={`badge ${u.isActive ? 'badge-success' : 'badge-gray'}`}>{u.isActive ? 'Activo' : 'Inactivo'}</span>
        {u.isSuperAdmin && <span className="badge badge-info">SuperAdmin</span>}
      </div>
    ) },
    { key: 'actions', header: '', className: 'col-actions', render: (u) => (
      <div className="dt-actions">
        <button className="btn btn-outline btn-sm" onClick={() => openUserEdit(u)}>
          <Pencil size={14} /> Ver / editar
        </button>
      </div>
    ) },
  ];

  const groupColumns = [
    { key: 'name', header: 'Nombre', className: 'dt-strong', fixed: true, render: (g) => g.name },
    { key: 'description', header: 'Descripción', render: (g) => g.description || '—' },
    { key: 'count', header: 'Nº miembros', className: 'num', render: (g) => g.members?.length || 0 },
    { key: 'members', header: 'Miembros', render: (g) => (g.members || []).map(m => m.user?.name).filter(Boolean).join(', ') || 'Sin miembros' },
    { key: 'actions', header: '', className: 'col-actions', render: (g) => (
      <div className="dt-actions">
        <button className="btn btn-outline btn-sm" onClick={() => openGroupEdit(g)}>
          <Pencil size={14} /> Editar
        </button>
        <button className="btn btn-outline btn-sm" onClick={() => handleDeleteGroup(g)}>
          <Trash2 size={14} /> Eliminar
        </button>
      </div>
    ) },
  ];

  const userFields = flUsers.fields(userColumns);
  const groupFields = flGroups.fields(groupColumns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Administración</h1>
          <p className="page-subtitle">Gestión de usuarios, roles y contratos</p>
        </div>
        {tenants[0] && (
          <button className="btn btn-outline" onClick={() => openTenantEdit(tenants[0])}>
            <Building2 size={16} /> Configuración de la empresa
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e2e8f0', paddingBottom: 0 }}>
        {[
          { key: 'users', label: 'Usuarios', icon: Users },
          { key: 'roles', label: 'Roles', icon: Shield },
          { key: 'groups', label: 'Grupos', icon: Users2 },
          { key: 'contracts', label: 'Contratos', icon: FileSignature }
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
              border: 'none', background: tab === t.key ? 'white' : 'transparent',
              borderBottom: tab === t.key ? '2px solid #1e40af' : '2px solid transparent',
              marginBottom: -2, cursor: 'pointer', fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? '#1e40af' : '#64748b', fontSize: '0.9rem'
            }}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* Users tab */}
      {tab === 'users' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Usuarios ({users.length})</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
              <VistaControles
                viewMode={usersViewMode}
                onViewModeChange={setUsersViewMode}
                fields={userFields}
                hidden={flUsers.hidden}
                onToggleField={flUsers.toggle}
                onReorderFields={flUsers.reorder}
              />
              <button className="btn btn-primary btn-sm" onClick={() => setShowUserModal(true)}><Plus size={16} /> Nuevo usuario</button>
            </div>
          </div>
          {usersViewMode === 'table' ? (
            <DataTable columns={flUsers.orderColumns(userColumns)} rows={users} hiddenKeys={flUsers.hidden} />
          ) : (
          <div className="items-grid">
            {users.map(u => (
              <div className="item-card" key={u.id}>
                <div className="item-card-header">
                  <div className="item-card-avatar" style={{ background: u.isActive ? 'var(--primary)' : 'var(--gray-400)' }}>
                    {u.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="item-card-title">{u.name}</div>
                    {!flUsers.isHidden('email') && (
                      <div className="item-card-subtitle" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                    )}
                  </div>
                  {!flUsers.isHidden('status') && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                      <span className={`badge ${u.isActive ? 'badge-success' : 'badge-gray'}`}>{u.isActive ? 'Activo' : 'Inactivo'}</span>
                      {u.isSuperAdmin && <span className="badge badge-info">SuperAdmin</span>}
                    </div>
                  )}
                </div>
                <div className="item-card-body">
                  {!flUsers.isHidden('phone') && (
                    <div className="item-card-row" style={flUsers.cardStyle('phone')}>
                      <span className="item-label">Teléfono</span>
                      <span style={{ textAlign: 'right', fontSize: '0.8rem' }}>{u.phone || '—'}</span>
                    </div>
                  )}
                  {!flUsers.isHidden('tenants') && (
                    <div className="item-card-row" style={flUsers.cardStyle('tenants')}>
                      <span className="item-label">Empresas</span>
                      <span style={{ textAlign: 'right', fontSize: '0.8rem' }}>{u.tenants?.map(t => t.tenant?.name).join(', ') || '—'}</span>
                    </div>
                  )}
                  {!flUsers.isHidden('groups') && (
                    <div className="item-card-row" style={flUsers.cardStyle('groups')}>
                      <span className="item-label">Grupos</span>
                      <span style={{ textAlign: 'right', fontSize: '0.8rem' }}>{(u.groups || []).map(g => g.group?.name).filter(Boolean).join(', ') || '—'}</span>
                    </div>
                  )}
                </div>
                <div className="item-card-footer">
                  <div className="item-card-actions">
                    <button className="btn btn-outline btn-sm" onClick={() => openUserEdit(u)}>
                      <Pencil size={14} /> Ver / editar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Roles tab */}
      {tab === 'roles' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Roles ({roles.length})</h2>
          </div>
          {[...roles].sort((a, b) => (a.rank ?? 100) - (b.rank ?? 100)).map(role => (
            <div key={role.id} style={{ marginBottom: 20, padding: 16, background: '#f8fafc', borderRadius: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <strong>{role.name}</strong>
                  <span className="badge badge-gray" style={{ marginLeft: 8 }}>rank {role.rank ?? 100}</span>
                  {role.isSystem && <span className="badge badge-info" style={{ marginLeft: 8 }}>Sistema</span>}
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{role.description}</div>
                </div>
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{role._count?.userTenants || 0} usuarios</span>
                <button className="btn btn-outline btn-sm" onClick={() => openRoleEdit(role)}>
                  <Pencil size={14} /> Ver / editar
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ fontSize: '0.8rem' }}>
                  <thead><tr><th>Módulo</th><th>Ver</th><th>Crear</th><th>Editar</th><th>Eliminar</th><th>Exportar</th></tr></thead>
                  <tbody>
                    {role.permissions?.map(p => (
                      <tr key={p.id}>
                        <td>{p.module?.label}</td>
                        <td>{p.canView ? '✓' : '—'}</td>
                        <td>{p.canCreate ? '✓' : '—'}</td>
                        <td>{p.canEdit ? '✓' : '—'}</td>
                        <td>{p.canDelete ? '✓' : '—'}</td>
                        <td>{p.canExport ? '✓' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Groups tab */}
      {tab === 'groups' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Grupos ({groups.length})</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
              <VistaControles
                viewMode={groupsViewMode}
                onViewModeChange={setGroupsViewMode}
                fields={groupFields}
                hidden={flGroups.hidden}
                onToggleField={flGroups.toggle}
                onReorderFields={flGroups.reorder}
              />
              <button className="btn btn-primary btn-sm" onClick={() => setShowGroupModal(true)}>
                <Plus size={16} /> Nuevo grupo
              </button>
            </div>
          </div>
          {groups.length === 0 && (
            <div style={{ padding: 16, color: '#64748b', fontSize: '0.85rem' }}>
              Aún no hay grupos creados. Los grupos permiten que usuarios del mismo grupo se vean los registros entre sí aunque tengan el mismo rango de rol.
            </div>
          )}
          {groupsViewMode === 'table' ? (
            <DataTable columns={flGroups.orderColumns(groupColumns)} rows={groups} hiddenKeys={flGroups.hidden} />
          ) : (
          <div className="items-grid">
            {groups.map(g => (
              <div className="item-card" key={g.id}>
                <div className="item-card-header">
                  <div className="item-card-avatar" style={{ background: 'var(--primary)' }}>
                    <Users2 size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="item-card-title">{g.name}</div>
                    {!flGroups.isHidden('description') && (
                      <div className="item-card-subtitle">{g.description || '—'}</div>
                    )}
                  </div>
                  {!flGroups.isHidden('count') && (
                    <span className="badge badge-gray">{g.members?.length || 0} miembros</span>
                  )}
                </div>
                <div className="item-card-body">
                  {!flGroups.isHidden('members') && (
                    <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                      {(g.members || []).map(m => m.user?.name).filter(Boolean).join(', ') || 'Sin miembros'}
                    </div>
                  )}
                </div>
                <div className="item-card-footer">
                  <div className="item-card-actions">
                    <button className="btn btn-outline btn-sm" onClick={() => openGroupEdit(g)}>
                      <Pencil size={14} /> Editar
                    </button>
                    <button className="btn btn-outline btn-sm" onClick={() => handleDeleteGroup(g)}>
                      <Trash2 size={14} /> Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Contratos: catálogos editables (tipos y estados) */}
      {tab === 'contracts' && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <h2 className="card-title">Tipos de contrato ({contractTypes.length})</h2>
            </div>
            <form onSubmit={handleCreateContractType} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label">Nuevo tipo</label>
                <input
                  className="form-input"
                  value={newContractType.name}
                  onChange={(e) => setNewContractType({ ...newContractType, name: e.target.value })}
                  placeholder="Ej: Mantenimiento"
                />
              </div>
              <div className="form-group" style={{ flex: '0 0 100px' }}>
                <label className="form-label">Orden</label>
                <input
                  className="form-input"
                  type="number"
                  value={newContractType.sortOrder}
                  onChange={(e) => setNewContractType({ ...newContractType, sortOrder: e.target.value })}
                />
              </div>
              <button type="submit" className="btn btn-primary"><Plus size={14} /> Añadir</button>
            </form>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '0.9rem' }}>
                <thead><tr><th></th><th style={{ textAlign: 'left' }}>Nombre</th><th>Orden</th><th>Activo</th><th>Uso</th></tr></thead>
                <tbody>
                  {contractTypes.map((item) => (
                    <tr key={item.id}>
                      <td style={{ width: 50 }}>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => handleDeleteContractType(item)}
                          style={{ color: '#dc2626' }}
                          disabled={(item._count?.contracts || 0) > 0}
                          title={(item._count?.contracts || 0) > 0 ? 'En uso, desactívalo en lugar de eliminar' : 'Eliminar'}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                      <td>
                        <input
                          className="form-input"
                          value={item.name}
                          onChange={(e) => setContractTypes((prev) => prev.map((i) => (i.id === item.id ? { ...i, name: e.target.value } : i)))}
                          onBlur={(e) => { if (e.target.value.trim() && e.target.value !== item.name) handleUpdateContractType(item.id, { name: e.target.value.trim() }); }}
                        />
                      </td>
                      <td style={{ width: 96 }}>
                        <input
                          className="form-input"
                          style={{ padding: '10px 8px', textAlign: 'center' }}
                          type="number"
                          value={item.sortOrder}
                          onChange={(e) => setContractTypes((prev) => prev.map((i) => (i.id === item.id ? { ...i, sortOrder: e.target.value } : i)))}
                          onBlur={(e) => handleUpdateContractType(item.id, { sortOrder: Number.parseInt(e.target.value, 10) || 0 })}
                        />
                      </td>
                      <td style={{ textAlign: 'center', width: 70 }}>
                        <input
                          type="checkbox"
                          checked={item.isActive}
                          onChange={(e) => handleUpdateContractType(item.id, { isActive: e.target.checked })}
                        />
                      </td>
                      <td style={{ textAlign: 'center', width: 70, color: '#64748b' }}>{item._count?.contracts || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Estados de contrato ({contractStatuses.length})</h2>
            </div>
            <form onSubmit={handleCreateContractStatus} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label">Nuevo estado</label>
                <input
                  className="form-input"
                  value={newContractStatus.name}
                  onChange={(e) => setNewContractStatus({ ...newContractStatus, name: e.target.value })}
                  placeholder="Ej: Vigente"
                />
              </div>
              <div className="form-group" style={{ flex: '0 0 100px' }}>
                <label className="form-label">Orden</label>
                <input
                  className="form-input"
                  type="number"
                  value={newContractStatus.sortOrder}
                  onChange={(e) => setNewContractStatus({ ...newContractStatus, sortOrder: e.target.value })}
                />
              </div>
              <button type="submit" className="btn btn-primary"><Plus size={14} /> Añadir</button>
            </form>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '0.9rem' }}>
                <thead><tr><th></th><th style={{ textAlign: 'left' }}>Nombre</th><th>Orden</th><th>Activo</th><th>Uso</th></tr></thead>
                <tbody>
                  {contractStatuses.map((item) => (
                    <tr key={item.id}>
                      <td style={{ width: 50 }}>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => handleDeleteContractStatus(item)}
                          style={{ color: '#dc2626' }}
                          disabled={(item._count?.contracts || 0) > 0}
                          title={(item._count?.contracts || 0) > 0 ? 'En uso, desactívalo en lugar de eliminar' : 'Eliminar'}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                      <td>
                        <input
                          className="form-input"
                          value={item.name}
                          onChange={(e) => setContractStatuses((prev) => prev.map((i) => (i.id === item.id ? { ...i, name: e.target.value } : i)))}
                          onBlur={(e) => { if (e.target.value.trim() && e.target.value !== item.name) handleUpdateContractStatus(item.id, { name: e.target.value.trim() }); }}
                        />
                      </td>
                      <td style={{ width: 96 }}>
                        <input
                          className="form-input"
                          style={{ padding: '10px 8px', textAlign: 'center' }}
                          type="number"
                          value={item.sortOrder}
                          onChange={(e) => setContractStatuses((prev) => prev.map((i) => (i.id === item.id ? { ...i, sortOrder: e.target.value } : i)))}
                          onBlur={(e) => handleUpdateContractStatus(item.id, { sortOrder: Number.parseInt(e.target.value, 10) || 0 })}
                        />
                      </td>
                      <td style={{ textAlign: 'center', width: 70 }}>
                        <input
                          type="checkbox"
                          checked={item.isActive}
                          onChange={(e) => handleUpdateContractStatus(item.id, { isActive: e.target.checked })}
                        />
                      </td>
                      <td style={{ textAlign: 'center', width: 70, color: '#64748b' }}>{item._count?.contracts || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showTenantEditModal && (
        <div className="modal-overlay" onClick={() => setShowTenantEditModal(false)}>
          <div className="modal modal--wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Editar empresa</h2>
              <button className="btn-icon" onClick={() => setShowTenantEditModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleUpdateTenant}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Nombre</label>
                  <input className="form-input" value={tenantEditForm.name} onChange={e => setTenantEditForm({ ...tenantEditForm, name: e.target.value })} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">NIF</label>
                    <input className="form-input" value={tenantEditForm.nif} onChange={e => setTenantEditForm({ ...tenantEditForm, nif: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-input" value={tenantEditForm.email} onChange={e => setTenantEditForm({ ...tenantEditForm, email: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Dirección</label>
                  <input className="form-input" value={tenantEditForm.address} onChange={e => setTenantEditForm({ ...tenantEditForm, address: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Ciudad</label>
                    <input className="form-input" value={tenantEditForm.city} onChange={e => setTenantEditForm({ ...tenantEditForm, city: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Provincia</label>
                    <input className="form-input" value={tenantEditForm.province} onChange={e => setTenantEditForm({ ...tenantEditForm, province: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Código postal</label>
                    <input className="form-input" value={tenantEditForm.postalCode} onChange={e => setTenantEditForm({ ...tenantEditForm, postalCode: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Teléfono</label>
                    <input className="form-input" value={tenantEditForm.phone} onChange={e => setTenantEditForm({ ...tenantEditForm, phone: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={tenantEditForm.isActive} onChange={e => setTenantEditForm({ ...tenantEditForm, isActive: e.target.checked })} />
                      <span className="form-label" style={{ margin: 0 }}>Empresa activa</span>
                    </label>
                  </div>
                </div>

                {/* Facturación */}
                <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 16, paddingTop: 16 }}>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: 12 }}>Facturación</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">IVA por defecto (%)</label>
                      <DecimalInput min="0" max="100" step="0.1"
                        value={tenantEditForm.default_tax_rate}
                        onValueChange={value => setTenantEditForm({ ...tenantEditForm, default_tax_rate: value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Retención IRPF por defecto (%)</label>
                      <DecimalInput min="0" max="100" step="0.1"
                        value={tenantEditForm.default_irpf_rate}
                        onValueChange={value => setTenantEditForm({ ...tenantEditForm, default_irpf_rate: value })} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Banco (nombre)</label>
                      <input className="form-input" value={tenantEditForm.billing_bank_name}
                        onChange={e => setTenantEditForm({ ...tenantEditForm, billing_bank_name: e.target.value })}
                        placeholder="Ej: OPENBANK" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">IBAN</label>
                      <input className="form-input" value={tenantEditForm.billing_bank_iban}
                        onChange={e => setTenantEditForm({ ...tenantEditForm, billing_bank_iban: e.target.value })}
                        placeholder="Ej: ES51 0073 0100 5105 8764 7319" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Prefijo por defecto para nuevas facturas</label>
                    <input className="form-input" value={tenantEditForm.invoice_prefix}
                      onChange={e => setTenantEditForm({ ...tenantEditForm, invoice_prefix: e.target.value })}
                      placeholder="Ej: F" />
                  </div>
                </div>

                <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 16, paddingTop: 16 }}>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: 12 }}>Email de facturas (SMTP)</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Servidor SMTP</label>
                      <input className="form-input" value={tenantEditForm.mail_host}
                        onChange={e => setTenantEditForm({ ...tenantEditForm, mail_host: e.target.value })}
                        placeholder="smtp.tudominio.com" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Puerto SMTP</label>
                      <input className="form-input" value={tenantEditForm.mail_port}
                        onChange={e => setTenantEditForm({ ...tenantEditForm, mail_port: e.target.value })}
                        placeholder="587" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Conexión segura</label>
                      <select className="form-input" value={tenantEditForm.mail_secure}
                        onChange={e => setTenantEditForm({ ...tenantEditForm, mail_secure: e.target.value })}>
                        <option value="false">No</option>
                        <option value="true">Sí</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Usuario SMTP</label>
                      <input className="form-input" value={tenantEditForm.mail_user}
                        onChange={e => setTenantEditForm({ ...tenantEditForm, mail_user: e.target.value })}
                        placeholder="usuario" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Contraseña SMTP</label>
                      <input className="form-input" type="password" value={tenantEditForm.mail_password}
                        onChange={e => setTenantEditForm({ ...tenantEditForm, mail_password: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Nombre remitente</label>
                      <input className="form-input" value={tenantEditForm.mail_from_name}
                        onChange={e => setTenantEditForm({ ...tenantEditForm, mail_from_name: e.target.value })}
                        placeholder="CRMllor" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email remitente</label>
                      <input type="email" className="form-input" value={tenantEditForm.mail_from_email}
                        onChange={e => setTenantEditForm({ ...tenantEditForm, mail_from_email: e.target.value })}
                        placeholder="facturacion@tudominio.com" />
                    </div>
                  </div>
                  <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 16, paddingTop: 16 }}>
                    <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: 12 }}>Copia en Enviados (IMAP)</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Servidor IMAP</label>
                        <input className="form-input" value={tenantEditForm.mail_imap_host}
                          onChange={e => setTenantEditForm({ ...tenantEditForm, mail_imap_host: e.target.value })}
                          placeholder="mail.tudominio.com" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Puerto IMAP</label>
                        <input className="form-input" value={tenantEditForm.mail_imap_port}
                          onChange={e => setTenantEditForm({ ...tenantEditForm, mail_imap_port: e.target.value })}
                          placeholder="993" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Conexión segura IMAP</label>
                        <select className="form-input" value={tenantEditForm.mail_imap_secure}
                          onChange={e => setTenantEditForm({ ...tenantEditForm, mail_imap_secure: e.target.value })}>
                          <option value="false">No</option>
                          <option value="true">Sí</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Usuario IMAP</label>
                        <input className="form-input" value={tenantEditForm.mail_imap_user}
                          onChange={e => setTenantEditForm({ ...tenantEditForm, mail_imap_user: e.target.value })}
                          placeholder="usuario@dominio.com" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Contraseña IMAP</label>
                        <input className="form-input" type="password" value={tenantEditForm.mail_imap_password}
                          onChange={e => setTenantEditForm({ ...tenantEditForm, mail_imap_password: e.target.value })} />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Carpeta de enviados IMAP</label>
                      <input className="form-input" value={tenantEditForm.mail_imap_sent_mailbox}
                        onChange={e => setTenantEditForm({ ...tenantEditForm, mail_imap_sent_mailbox: e.target.value })}
                        placeholder="Déjalo vacío para detección automática" />
                    </div>
                  </div>
                </div>

                {/* Token asesoría — el portal lee la API con este Bearer */}
                <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 16, paddingTop: 16 }}>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: 12 }}>Portal asesoría</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Token (Bearer) que usa el portal</label>
                      <input className="form-input" type="password" value={tenantEditForm.sync_asesoria_token}
                        onChange={e => setTenantEditForm({ ...tenantEditForm, sync_asesoria_token: e.target.value })} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowTenantEditModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ marginLeft: 'auto' }}>Guardar cambios</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showUserEditModal && (
        <div className="modal-overlay" onClick={() => setShowUserEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Editar usuario</h2>
              <button className="btn-icon" onClick={() => setShowUserEditModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Nombre</label>
                  <input className="form-input" value={userEditForm.name} onChange={e => setUserEditForm({ ...userEditForm, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-input" value={userEditForm.email} onChange={e => setUserEditForm({ ...userEditForm, email: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input type="tel" className="form-input" placeholder="+34..." value={userEditForm.phone} onChange={e => setUserEditForm({ ...userEditForm, phone: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Nueva contraseña (opcional)</label>
                  <input type="password" className="form-input" value={userEditForm.password} onChange={e => setUserEditForm({ ...userEditForm, password: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={userEditForm.isSuperAdmin} onChange={e => setUserEditForm({ ...userEditForm, isSuperAdmin: e.target.checked })} />
                      <span className="form-label" style={{ margin: 0 }}>Super Administrador</span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={userEditForm.isActive} onChange={e => setUserEditForm({ ...userEditForm, isActive: e.target.checked })} />
                      <span className="form-label" style={{ margin: 0 }}>Usuario activo</span>
                    </label>
                  </div>
                </div>
                {groups.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Grupos</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {groups.map(g => {
                        const checked = userEditForm.groupIds.includes(g.id);
                        return (
                          <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.85rem' }}>
                            <input type="checkbox" checked={checked} onChange={() => {
                              const next = checked
                                ? userEditForm.groupIds.filter(id => id !== g.id)
                                : [...userEditForm.groupIds, g.id];
                              setUserEditForm({ ...userEditForm, groupIds: next });
                            }} />
                            {g.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowUserEditModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar cambios</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRoleEditModal && (
        <div className="modal-overlay" onClick={() => setShowRoleEditModal(false)}>
          <div className="modal modal--xl" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Editar rol</h2>
              <button className="btn-icon" onClick={() => setShowRoleEditModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleUpdateRole}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Nombre</label>
                    <input className="form-input" value={roleEditForm.name} onChange={e => setRoleEditForm({ ...roleEditForm, name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Descripción</label>
                    <input className="form-input" value={roleEditForm.description} onChange={e => setRoleEditForm({ ...roleEditForm, description: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Rango</label>
                    <input className="form-input" type="number" min="1" step="1"
                      value={roleEditForm.rank}
                      onChange={e => setRoleEditForm({ ...roleEditForm, rank: e.target.value })} />
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>
                      Menor número = más alto. Iguales = pares (no se ven entre sí salvo mismo grupo).
                    </div>
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th>Módulo</th>
                        <th>Ver</th>
                        <th>Crear</th>
                        <th>Editar</th>
                        <th>Eliminar</th>
                        <th>Exportar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roleEditForm.permissions.map((permission) => (
                        <tr key={permission.moduleId}>
                          <td>{permission.moduleLabel}</td>
                          <td><input type="checkbox" checked={permission.canView} onChange={e => updateRolePermission(permission.moduleId, 'canView', e.target.checked)} /></td>
                          <td><input type="checkbox" checked={permission.canCreate} onChange={e => updateRolePermission(permission.moduleId, 'canCreate', e.target.checked)} /></td>
                          <td><input type="checkbox" checked={permission.canEdit} onChange={e => updateRolePermission(permission.moduleId, 'canEdit', e.target.checked)} /></td>
                          <td><input type="checkbox" checked={permission.canDelete} onChange={e => updateRolePermission(permission.moduleId, 'canDelete', e.target.checked)} /></td>
                          <td><input type="checkbox" checked={permission.canExport} onChange={e => updateRolePermission(permission.moduleId, 'canExport', e.target.checked)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowRoleEditModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar cambios</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nuevo usuario</h2>
              <button className="btn-icon" onClick={() => setShowUserModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Nombre *</label>
                  <input className="form-input" value={userForm.name} onChange={e => setUserForm({ ...userForm, name: e.target.value })} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input type="email" className="form-input" value={userForm.email} onChange={e => setUserForm({ ...userForm, email: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contraseña *</label>
                    <input type="password" className="form-input" value={userForm.password} onChange={e => setUserForm({ ...userForm, password: e.target.value })} required minLength={6} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input type="tel" className="form-input" placeholder="+34..." value={userForm.phone} onChange={e => setUserForm({ ...userForm, phone: e.target.value })} />
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={userForm.isSuperAdmin} onChange={e => setUserForm({ ...userForm, isSuperAdmin: e.target.checked })} />
                    <span className="form-label" style={{ margin: 0 }}>Super Administrador</span>
                  </label>
                </div>
                {!userForm.isSuperAdmin && (
                  <div className="form-group">
                    <label className="form-label">Rol *</label>
                    <select
                      className="form-input"
                      value={userForm.tenantAssignments[0]?.roleId || ''}
                      onChange={(e) => {
                        const roleId = parseInt(e.target.value, 10);
                        const tenantId = tenants[0]?.id;
                        if (roleId && tenantId) {
                          setUserForm({ ...userForm, tenantAssignments: [{ tenantId, roleId }] });
                        } else {
                          setUserForm({ ...userForm, tenantAssignments: [] });
                        }
                      }}
                      required={!userForm.isSuperAdmin}
                    >
                      <option value="">Seleccionar rol</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                )}
                {groups.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Grupos</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {groups.map(g => {
                        const checked = userForm.groupIds.includes(g.id);
                        return (
                          <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.85rem' }}>
                            <input type="checkbox" checked={checked} onChange={() => {
                              const next = checked
                                ? userForm.groupIds.filter(id => id !== g.id)
                                : [...userForm.groupIds, g.id];
                              setUserForm({ ...userForm, groupIds: next });
                            }} />
                            {g.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowUserModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Crear usuario</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showGroupModal && (
        <div className="modal-overlay" onClick={() => setShowGroupModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nuevo grupo</h2>
              <button className="btn-icon" onClick={() => setShowGroupModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateGroup}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Nombre</label>
                  <input className="form-input" value={groupForm.name} onChange={e => setGroupForm({ ...groupForm, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Descripción</label>
                  <input className="form-input" value={groupForm.description} onChange={e => setGroupForm({ ...groupForm, description: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Miembros</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, maxHeight: 240, overflowY: 'auto' }}>
                    {users.map(u => {
                      const checked = groupForm.userIds.includes(u.id);
                      return (
                        <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.85rem' }}>
                          <input type="checkbox" checked={checked} onChange={() => {
                            const next = checked
                              ? groupForm.userIds.filter(id => id !== u.id)
                              : [...groupForm.userIds, u.id];
                            setGroupForm({ ...groupForm, userIds: next });
                          }} />
                          {u.name}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowGroupModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Crear grupo</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showGroupEditModal && (
        <div className="modal-overlay" onClick={() => setShowGroupEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Editar grupo</h2>
              <button className="btn-icon" onClick={() => setShowGroupEditModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleUpdateGroup}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Nombre</label>
                  <input className="form-input" value={groupEditForm.name} onChange={e => setGroupEditForm({ ...groupEditForm, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Descripción</label>
                  <input className="form-input" value={groupEditForm.description} onChange={e => setGroupEditForm({ ...groupEditForm, description: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Miembros</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, maxHeight: 240, overflowY: 'auto' }}>
                    {users.map(u => {
                      const checked = groupEditForm.userIds.includes(u.id);
                      return (
                        <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.85rem' }}>
                          <input type="checkbox" checked={checked} onChange={() => {
                            const next = checked
                              ? groupEditForm.userIds.filter(id => id !== u.id)
                              : [...groupEditForm.userIds, u.id];
                            setGroupEditForm({ ...groupEditForm, userIds: next });
                          }} />
                          {u.name}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowGroupEditModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar cambios</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
